import axios from 'axios'
import wrappers from '../utils/wrappers'
import { useConnectionStatus } from '../composables/useConnectionStatus'
import { getAdaptiveHttpTimeoutMs } from '../utils/networkAdaptation'
import { isConnectivityError } from '../utils/isConnectivityError'
import type {
  GroupCreatePayload,
  GroupChatHead,
  GroupChatMessage,
  GroupMemberProfile,
  GroupSprintCreatePayload,
  GroupSprintOffer,
  GroupSprintSummary,
  GroupSummary,
  GroupUpdatePayload,
  GroupsAdminQuota,
} from '../types/groups'

const url = `${import.meta.env.VITE_API_URL}/v1/groups`

export default {
  create(payload: GroupCreatePayload) {
    return axios
      .post(url, payload, { timeout: 8000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSummary })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo crear el grupo'),
      )
  },

  update(groupId: string, payload: GroupUpdatePayload) {
    return axios
      .patch(`${url}/${groupId}`, payload, { timeout: 8000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSummary })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo actualizar el grupo'),
      )
  },

  get(groupId: string) {
    return axios
      .get(`${url}/${groupId}`, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSummary })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo cargar el grupo'),
      )
  },

  listMine(options?: { silent?: boolean }) {
    return axios
      .get(`${url}/me`, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSummary[] })
      .catch((error: any) => {
        if (options?.silent) {
          return Promise.reject(error)
        }
        return wrappers.handleEscribonautaError(error, 'No se pudieron cargar tus grupos')
      })
  },

  getMyQuota() {
    return axios
      .get(`${url}/me/quota`, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupsAdminQuota })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo cargar el cupo de grupos'),
      )
  },

  discover(params: { q?: string; genre?: string; sort?: 'recent' | 'featured'; limit?: number } = {}) {
    return axios
      .get(`${url}/discover`, { params, timeout: 8000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSummary[] })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudieron buscar grupos'),
      )
  },

  join(groupId: string) {
    return axios
      .post(`${url}/${groupId}/join`, {}, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSummary })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo unir al grupo'),
      )
  },

  leave(groupId: string) {
    return axios
      .post(`${url}/${groupId}/leave`, {}, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo salir del grupo'),
      )
  },

  invite(groupId: string, userId: string) {
    return axios
      .post(`${url}/${groupId}/invite`, { userId }, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupMemberProfile })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo enviar la invitación'),
      )
  },

  acceptInvite(groupId: string) {
    return axios
      .post(`${url}/${groupId}/accept-invite`, {}, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSummary })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo aceptar la invitación'),
      )
  },

  rejectInvite(groupId: string) {
    return axios
      .post(`${url}/${groupId}/reject-invite`, {}, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo rechazar la invitación'),
      )
  },

  removeMember(groupId: string, userId: string) {
    return axios
      .delete(`${url}/${groupId}/members/${userId}`, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo eliminar al miembro'),
      )
  },

  transferAdmin(groupId: string, userId: string) {
    return axios
      .put(`${url}/${groupId}/transfer-admin`, { userId }, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSummary })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo transferir la administración'),
      )
  },

  delete(groupId: string) {
    return axios
      .delete(`${url}/${groupId}`, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response))
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo disolver el grupo'),
      )
  },

  listMembers(groupId: string) {
    return axios
      .get(`${url}/${groupId}/members`, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupMemberProfile[] })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudieron cargar los miembros'),
      )
  },

  getSprintStatus(groupId: string) {
    return axios
      .get(`${url}/${groupId}/sprints/status`, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSprintSummary | null })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo cargar el sprint'),
      )
  },

  createSprint(groupId: string, payload: GroupSprintCreatePayload) {
    return axios
      .post(`${url}/${groupId}/sprints`, payload, { timeout: 8000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSprintSummary })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo crear el sprint'),
      )
  },

  cancelSprint(groupId: string, sprintId: string) {
    return axios
      .post(`${url}/${groupId}/sprints/${sprintId}/cancel`, {}, { timeout: 8000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSprintSummary })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo cancelar el sprint'),
      )
  },

  joinSprint(groupId: string, sprintId: string) {
    return axios
      .post(`${url}/${groupId}/sprints/${sprintId}/join`, {}, { timeout: 8000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSprintSummary })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo unir al sprint'),
      )
  },

  switchSprint(groupId: string, sprintId: string) {
    return axios
      .post(`${url}/${groupId}/sprints/${sprintId}/switch`, {}, { timeout: 8000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSprintSummary })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo cambiar de sprint'),
      )
  },

  listSprintHistory(groupId: string) {
    return axios
      .get(`${url}/${groupId}/sprints/history`, { timeout: 8000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSprintSummary[] })
      .catch((error: any) =>
        wrappers.handleEscribonautaError(error, 'No se pudo cargar el historial de sprints'),
      )
  },

  listMyActiveSprints(requestOptions?: { silentConnectivityError?: boolean }) {
    return axios
      .get(`${url}/me/sprints/active`, {
        timeout: getAdaptiveHttpTimeoutMs(8_000),
        action: 'groups.listMyActiveSprints',
      })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupSprintOffer[] })
      .catch((error: any) => {
        if (requestOptions?.silentConnectivityError && isConnectivityError(error)) {
          return Promise.reject(error)
        }
        return wrappers.handleEscribonautaError(error, 'No se pudieron cargar los sprints activos')
      })
  },

  sendMessage(groupId: string, content: string) {
    return axios
      .post(`${url}/${groupId}/messages`, { content }, { timeout: 8000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupChatMessage })
      .catch((error: any) => {
        const status = error?.response?.status
        const errorCode =
          error?.response?.data?.errorCode || error?.response?.data?.data?.errorCode
        if (status === 429 || errorCode === 'GROUP_CHAT_RATE_LIMIT') {
          return Promise.reject(error)
        }
        return wrappers.handleEscribonautaError(error, 'No se pudo enviar el mensaje')
      })
  },

  listMessages(
    groupId: string,
    params: { beforeId?: string; afterId?: string; limit?: number } = {},
  ) {
    return axios
      .get(`${url}/${groupId}/messages`, { params, timeout: 8000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupChatMessage[] })
      .catch((error: any) => Promise.reject(error))
  },

  getChatHead(groupId: string) {
    if (!useConnectionStatus().isOnline.value) {
      const offlineError = new Error('offline') as Error & { code?: string }
      offlineError.code = 'ERR_NETWORK'
      return Promise.reject(offlineError)
    }
    return axios
      .get(`${url}/${groupId}/chat/head`, { timeout: 5000 })
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupChatHead })
      .catch((error: any) => Promise.reject(error))
  },

  markChatRead(groupId: string, messageId?: string) {
    return axios
      .post(
        `${url}/${groupId}/chat/read`,
        messageId ? { messageId } : {},
        { timeout: 5000 },
      )
      .then((response: any) => wrappers.handleEscribonautaResponse(response) as { data: GroupChatHead })
      .catch((error: any) => Promise.reject(error))
  },
}
